export type ScreeningService = 'healix' | 'cega';
export type TravellerType = 'adult' | 'child';
export type SchemeType = 'Single Trip' | 'Annual Multi Trip' | 'Longstay';
export type TripType = 'single' | 'annual' | 'longstay';
export type ChannelType = 'AGG' | 'B2C';

export class QuoteStatus {
  public static readonly APPLIED = 'applied';
  public static readonly QUOTE = 'quote';
  public static readonly RENEWAL = 'renewal';
  public static readonly RENEWAL_INVITED = 'renewal invited';
  public static readonly LAPSED = 'lapsed';
  public static readonly EXPIRED = 'expired';
  public static readonly DECLINED = 'declined';
  public static readonly REFERRED = 'referred';
  public static readonly REFERRAL_APPROVED = 'referral approved';
  public static readonly ISSUED = 'issued';
  public static readonly ON_COVER = 'on cover';
  public static readonly CANCELLED = 'cancelled';

  public static translate(status: string) {
    switch (status) {
      case this.RENEWAL: return 'Renouvellement';
      case this.RENEWAL_INVITED: return 'En attente de confirmation de renouvellement';
      case this.LAPSED: return 'Résilié';
      case this.EXPIRED: return 'Résilié'; // as we’re on “tacit renewal” we don’t need it but only “lapsed”
      case this.DECLINED: return 'Refusé';
      case this.REFERRED: return 'Référé';
      case this.REFERRAL_APPROVED: return 'Referral approuvé';
      case this.QUOTE: return 'Devis';
      case this.ISSUED: return 'Émis';
      case this.ON_COVER: return 'En cours';
      case this.CANCELLED: return 'Résilié';

      default: return status;
    }
  }
}

export interface IApplicationOptions {
  calculate?: boolean;
  patch?: boolean;
}

export interface IClaim {
  date: string;
  type: string;
  cost: number;
  fault: string;
  open: string;
}

export interface IConviction {
  code: string;
  description: string;
  date: string;
  points: number;
  banned: number;
  fine: number;
}

export interface IAddress {
  line1: string;
  line2?: string;
  line3?: string;
  town?: string;
  county?: string;
  postcode: string;
}

export interface ITraveller {
  id?: string;
  title: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  dob?: string;
  age?: number;
  address?: IAddress;
  screening?: {
    meta: any;
  };
  screeningService?: ScreeningService;
  screeningSessionId?: string;
  type: TravellerType;
}
export interface ICustomerMetadata {
  status: string;
  microEnterprise: string;
  tradingName: string;
  companyRegistrationNumber?: string;
  year: string;
  typeOfLicence: string;
  lengthOfLicence: string;
  claims?: IClaim[];
  convictions?: IConviction[];
}

export interface ICustomer {
  id?: string;
  title: string;
  firstName: string;
  lastName: string;
  customerName?: string;
  dob?: string;
  address?: IAddress;
  email?: string;
  emailConfirmation ?: string;
  companyName ?: string;
  country?: string;
  phone1?: string;
  phone2?: string;
  deceased?: boolean;
  status?: string;
  metadata?: ICustomerMetadata;
  policies?: IPolicy[];
  policyApplications?: IPolicyApplication[];
  renewals?: IPolicyApplication[];
  password?: string;
  passwordConfirmation?: string;
}

export interface IDeclaration {
  question: string;
  answer: string;
}

export interface ICountryState {
  id: string;
  name: string;
}

export interface IVehicleLookup {
  make: string;
  model: string;
  year: string;
  value?: number;
  weight?: number;
}

export interface ICard {
  cardNumber: string;
  cvv: string;
  expiryMonth: string;
  expiryYear: string;
}

export interface IVehicleDriver {
  driver: string;
}

export interface IVehicle {
  id: string;
  bodyType: string;
  year: string;
  value?: number;
  roadRegistered: string;
  registrationNumber: string;
  registrationNumberSearched?: boolean;
  make: string;
  model: string;
  weight: string;
  drivingRestriction: string;
  drivingRestrictionCalculated: string;
  drivers: IVehicleDriver[];
  showExtendedUse: string;
  extendedUses: boolean[];
  equineTradeExtendedUsers?: IVehicleDriver[];
  nonEquineTradeExtendedUsers?: IVehicleDriver[];
  greenCard: string;
  mileage: string;
  proposerOwner: string;
  owner: string;
  ownerName: string;
  proposerOvernight: string;
  postcode: string;
  tracker: string;
  coverType: string;
  breakdownCover: string;
  age?: string;
  use: string;
  ncd: string;
  excessTheftLockedBuilding?: number;
  excessTheftOther?: number;
  excessAdft?: number;
}

export interface IDriver {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  dob: string;
  proposer: boolean;
  typeOfLicence: string;
  lengthOfLicence: string;
  nonEquineTrade: string;
  vehicles: any[];
  claims?: IClaim[];
  convictions?: IConviction[];
}

export interface IPolicy {
  policyNumber: string;
  insured: string;
  status: string;
  id: string;
  version: number;
  productName: string;
  userName: string;
  brokerName: string;
  premium?: number;
  channelName: string;
  minimumDocumentCount: number;
  isInsideCancellationPeriod: boolean;
  metadata: any;
  commencesAt: string;
  expiresAt: string;
  issuedAt: string;
  cancelledAt: string;
  documents: IDocument[];
  transactions: ITransaction[];
  supersededPolicy?: IPolicy;
  totalPremiumValue: number;
}

export interface IPolicyApplication {
  id?: string;
  quotationTypes?: string;
  residency?: string;
  startDate: string;
  endDate: string;
  quotes?: IQuotes;
  schemeId?: string;
  calculateAllSchemes?: boolean;
  sendSms?: boolean;
  errors?: any[];
  purchaseDeclaration?: boolean;
  paymentToken?: string;
  payOnAccount?: boolean;
  steps: string[];
  password?: string;
  passwordConfirmation?: string;
  customer: ICustomer;
  quotePremiums?: IPremium[];
  premiums?: IPremium[];
  quoteReference?: string;
  policyDeclaration?: boolean;
  status?: string;
  documents?: IDocument[];
  generateDocuments: boolean;
  emailSend: boolean;
  channel?: IChannel;
  payment?: IPaymentInfo;
  createAccount?: boolean;
  brokerId?: string;
  renewingPolicyNumber?: string;
  renewal?: boolean;
  quoteType?: string;
  card?: ICard;
  reuseCustomerAddress?: boolean;

  declaration: {
    1: string;
    2: string;
    3: string;
    5: string;
    6: string;
    7: string;
    8: string;
    9: string;
    10: string;
    11: string;
    12: string;
    13: string;
    14: string;
    15?: string;
    16?: string;
    17?: string;
    18?: string;
  };
  declarationTradeVehicle?: boolean;
  vehicleCount: number;
  vehicles: IVehicle[];
  personalCustomer: boolean;
  customerType: string;
  tenure: number;
  voluntaryExcess: string;
  voluntaryExcessEnabled: string;
  driverCount: number;
  drivers: IDriver[];
  purchaseDeclarationString?: string;
  quickQuote: boolean;
  contractingPercentage: string;
  nonEquineTrade: string;

  paymentSchedule: string;

  backdatedCover: boolean;
  insured: ICustomer;
  paymentType?: string;
  paymentDate?: string;
  ignoreReferrals: {
    [key: string]: string;
  };
  excessOverrides: {
    vehicle: any;
    driver: any;
  };
  rateOverrides: {
    vehicle: any;
    overall: number;
    driver: any;
  };
  compulsoryExcess?: string;
  postcodeReferrals: {
    decline?: boolean;
    refer?: boolean;
  };
  vehicleReferrals: {
    bodyTypeRefer?: boolean,
    bodyTypeDecline?: boolean,
    vehicleAge?: boolean,
    vehicleValue?: boolean,
  };
  tempDriver: {
    title: string,
    firstName: string,
    lastName: string,
    hasNoNameYet: boolean,
  };
}

export interface IPaymentInfo {
  cardNumber: string;
  cardName: string;
  cardExpiry: string;
  cvv: string;
}

export interface IProduct {
  id: string;
  name: string;
  productType: string;
  productCode: string;
  status: string;
  summary: string;
  description: string;
  icon: string;
  metadata: any;
  documents: {
    [name: string]: string;
  };
}

export interface IQuotes {
  [levelType: string]: {
    [tripType: string]: IPremium;
  };
}

export interface IScheme {
  id: string;
  coverLevel: string;
  status: string;
  logo: string;
  name: string;
  insurer: string;
  documents: {
    [name: string]: string;
  };
  includedOptions?: object;
  metadata?: any;
}

export interface IBenefit {
  name: string;
  text: string;
  inverse: boolean;
  excess: string;
  displayName: string;
  shortName: string;
  description: any;
  subBenefits?: ISubBenefit[];
}

export interface ISubBenefit {
  name: string;
  text: string;
  excess: string;
}
export interface IFee {
  name: string;
  rate: number;
  rate_type: string;
}

export interface IInformation {
  standardExcess: string;
  boltOns: {
    [key: string]: any;
  };
  upgrade: {
    schemeId: string;
    coverLevel: string;
    amount: {
      total: number;
      difference: number;
    };
  };
  maxTripDuration?: number;
  startDate?: string;
  region?: string;
  tripType?: string;
  fees: {
    [name: string]: IFee;
  };
  breakdownCover: {
    included: {
      [key: string]: boolean;
    };
    gross: {
      [key: string]: number;
    };
    tax: {
      [key: string]: number;
    };
    value: {
      [key: string]: number;
    };
  };
}

export interface IPremium {
  id: string;
  schemeType: SchemeType;
  calculatedAt: string;
  validUntil: string;
  currency: string;
  tax: number;
  taxRate: number;
  exTax: number;
  principal: number;
  gross: number;
  grossExAdditional: number;
  stampDuty: {
    [name: string]: number;
  };
  benefits: IBenefit[];
  scheme: IScheme;
  information: IInformation;
  errors: any;
  declines?: IDeclines[];
  referrals?: IReferrals[];
}

export interface IDeclines {
  name: string;
  reason: string;
}

export interface IReferrals {
  name: string;
  reason: string;
}

export interface IStep {
  title: string;
  key: string;
  route: string;
  saveApplication: boolean;
  saveApplicationWithPatch: boolean;
  calculatePremium: boolean;
  hidden?: boolean;
  parent?: string;
  enabled: any;
  showReferrals ?: boolean;
  keepDataWhenLeaving ?: boolean;
}

export interface IChannel {
  id: string;
  name: string;
  channelType: ChannelType;
  key: string;
  whitelabel: boolean;
  products: IProduct[];
  productDocuments: IDocument[];
  baseName: string;
  phone: string;
  footer: string;
  openingHours: string;
  gtmId: string;
  cseScript: string;
  offlineSchedule?: IOfflineSchedule[];
  steps?: IStep[];
  channelLogo?: string;
  additionalLogo?: string;
  ewayEncryptionKey?: string;
  live?: boolean;
}

export interface IOfflineSchedule {
  type: string;
  message: string;
  redirectTo?: string;
  startAt?: string;
  endAt?: string;
}

export interface IPostcodeLookup {
  id: string;
  line: string;
}

export interface IPostcodeAddress {
  organisation: string;
  line1: string;
  line2: string;
  line3: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
}

export interface IDocument {
  id: string;
  name: string;
  filePath: string;
  shortName: string;
  displayName: string;
}

export interface ITrackingLink {
  id: string;
  name: string;
  phone: string;
  status: string;
}

export interface ITransaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  transactedAt: string;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/data/api/models.ts